// Travel & Transport billing config (Build 3a). Two sections:
//   • Global defaults (fin_settings) + "Update Uninvoiced Shifts" bulk re-stamp.
//   • Per-service-type datagrid (fin_travel_config): caps, rates, NDIS codes, therapy %.
// NDIS rule surfaced: a cap of 0 = NOT billable (overrides shift overrides).
// Also rendered inside Settings as <TravelTransport embedded />: no page frame and no tiles there.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Save, Info, RefreshCw, Route, SlidersHorizontal } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, Field, FormRow, Switch,
  money, num as fmtNum, fmtDate,
} from '../../components/ui';
import { TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, codeLabel } from './financeKit';

// Coerces a typed value for the API: blank → 0. The payloads below depend on it; leave it as is.
const num = (v) => v === '' || v == null ? 0 : Number(v);

const CELL = 'px-2 py-1.5 align-middle';

export default function TravelTransport({ embedded = false }) {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [bulkDate, setBulkDate] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [transportItems, setTransportItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, s] = await Promise.all([api.get('/financials/travel-config'), api.get('/financials/settings')]);
      setRows(cfg.data || []);
      setSettings(s.data || {});
    } catch (e) {
      setError(e.response?.data?.message || "We couldn't load the travel and transport settings. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { api.get('/price-books/transport-items').then(r => setTransportItems(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const patchRow = (id, key, val) => setRows(rs => rs.map(r => r.id === id ? { ...r, [key]: val } : r));
  const saveRow = async (r) => {
    const name = codeLabel(r.shift_type) || r.shift_type;
    setSavingId(r.id);
    try { await api.put(`/financials/travel-config/${r.id}`, r); toast.success(`${name} saved`); }
    catch (e) { toast.error(e.response?.data?.message || `Couldn't save ${name}. Try again.`); } finally { setSavingId(null); }
  };

  const saveDefaults = async () => {
    setSavingDefaults(true);
    try {
      await api.put('/financials/travel-settings', {
        default_travel_km_rate: num(settings.default_travel_km_rate),
        default_travel_time_rate: num(settings.default_travel_time_rate),
        default_transport_km_rate: num(settings.default_transport_km_rate),
        km_rate_company_van: num(settings.km_rate_company_van),
        km_rate_modified: num(settings.km_rate_modified),
        default_transport_item_number: (settings.default_transport_item_number || '').trim(),
        travel_apply_to_new_shifts: !!settings.travel_apply_to_new_shifts,
        therapy_travel_pct: num(settings.therapy_travel_pct),
        therapy_travel_from: settings.therapy_travel_from || '2025-07-01',
      });
      toast.success('Travel defaults saved');
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't save the defaults. Try again."); } finally { setSavingDefaults(false); }
  };

  const runBulk = async () => {
    if (!bulkDate) { setBulkError('Pick the first shift date to update.'); return; }
    if (!await confirm(
      `The travel and transport rates on this page replace the rates on every uninvoiced shift from ${fmtDate(bulkDate)}, and those shifts are recalculated. Invoiced shifts are never changed.`,
      { title: 'Re-stamp uninvoiced shifts?', confirmLabel: 'Re-stamp shifts' },
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/financials/travel-settings/bulk-update', { from_date: bulkDate });
      toast.success(`Updated ${data.updated} shift${data.updated === 1 ? '' : 's'}${data.capped ? ` (${data.remaining} remaining — run again)` : ''}`);
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't re-stamp the shifts. Try again."); } finally { setBulkBusy(false); }
  };

  const setField = (key) => (e) => setSettings(s => ({ ...s, [key]: e.target.value }));
  const wrap = embedded ? 'space-y-6' : PAGE;

  const intro = (
    <p className="max-w-3xl text-[13px] leading-relaxed text-slate-600">
      Caps, NDIS codes and default rates. <b className="font-semibold text-slate-800">Travel</b> is worker time billed to the plan;{' '}
      <b className="font-semibold text-slate-800">transport</b> is client-in-car km billed to a transport fund. A cap of 0 means that part is{' '}
      <b className="font-semibold text-slate-800">not billable</b>.
    </p>
  );

  if (loading) {
    return <div className={wrap}>{intro}<div className="card"><TableSkeleton rows={6} cols={6} /></div></div>;
  }
  if (error) {
    return <div className={wrap}>{intro}<div className="card"><ErrorState message={error} onRetry={load} /></div></div>;
  }

  const therapyCount = rows.filter(r => r.is_therapy).length;
  const billableTransport = rows.filter(r => num(r.max_transport_km) > 0).length;

  return (
    <div className={wrap}>
      {intro}

      {/* Summary */}
      {!embedded && rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile label="Service types" value={fmtNum(rows.length)} accent="slate" sub="Configured" />
          <KpiTile label="Billable transport" value={fmtNum(billableTransport)} accent="slate" sub="With a transport km cap above 0" />
          <KpiTile label="Therapy service types" value={fmtNum(therapyCount)} accent="slate" sub="Use the therapy travel %" />
          <KpiTile label="Client km rate, own car" value={`${money(num(settings.default_transport_km_rate))}/km`} accent="primary"
            sub="Charged when the worker drives their own car" />
        </div>
      )}

      {/* Global defaults */}
      <SectionCard title="Global defaults" icon={SlidersHorizontal} bodyClass="p-5">
        <div className="space-y-5">
          <FormRow cols={3}>
            <Field label="Default travel km rate ($/km)">
              {(id) => <input id={id} type="number" step="0.01" className="input-field" value={settings.default_travel_km_rate ?? ''} onChange={setField('default_travel_km_rate')} />}
            </Field>
            <Field label="Default travel time rate ($/hr)" hint="0 bills travel time at the service's hourly rate.">
              {(id) => <input id={id} type="number" step="0.01" className="input-field" value={settings.default_travel_time_rate ?? ''} onChange={setField('default_travel_time_rate')} />}
            </Field>
            <Field label="Client km rate, own car ($/km)" hint="Worker's own car. The NDIS cap is about $0.99 per km.">
              {(id) => <input id={id} type="number" step="0.01" className="input-field" value={settings.default_transport_km_rate ?? ''} onChange={setField('default_transport_km_rate')} />}
            </Field>
            <Field label="Client km rate, company van ($/km)" hint="Provider's van. The worker isn't paid km.">
              {(id) => <input id={id} type="number" step="0.01" className="input-field" value={settings.km_rate_company_van ?? ''} onChange={setField('km_rate_company_van')} />}
            </Field>
            <Field label="Client km rate, modified vehicle ($/km)" hint="Accessibility-modified (WAV) vehicle at actual cost, usually the highest rate.">
              {(id) => <input id={id} type="number" step="0.01" className="input-field" value={settings.km_rate_modified ?? ''} onChange={setField('km_rate_modified')} />}
            </Field>
            <Field label="Default transport item number" hint="NDIS item stamped on the mileage line.">
              {(id) => <input id={id} list="ndis-transport-items" className="input-field font-mono text-sm" value={settings.default_transport_item_number ?? ''} onChange={setField('default_transport_item_number')} />}
            </Field>
            <Field label="Therapy travel %" hint="For example 50, from the date beside it.">
              {(id) => <input id={id} type="number" step="1" className="input-field" value={settings.therapy_travel_pct ?? ''} onChange={setField('therapy_travel_pct')} />}
            </Field>
            <Field label="Therapy % effective from">
              {(id) => <input id={id} type="date" className="input-field" value={(settings.therapy_travel_from || '').slice(0, 10)} onChange={setField('therapy_travel_from')} />}
            </Field>
            <div className="flex items-end">
              <button type="button" role="switch" aria-checked={!!settings.travel_apply_to_new_shifts}
                onClick={() => setSettings(s => ({ ...s, travel_apply_to_new_shifts: !s.travel_apply_to_new_shifts }))}
                className="flex min-h-[42px] items-center gap-3 rounded-lg px-1 text-left text-sm text-slate-700 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                <Switch decorative checked={!!settings.travel_apply_to_new_shifts} />
                Apply defaults to new shifts
              </button>
            </div>
          </FormRow>

          <div className="flex flex-wrap items-end justify-between gap-3 border-t border-slate-100 pt-4">
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Update uninvoiced shifts from" error={bulkError || undefined}>
                {(id) => (
                  <input id={id} type="date" className="input-field !w-auto" value={bulkDate} aria-invalid={!!bulkError}
                    onChange={e => { setBulkDate(e.target.value); if (bulkError) setBulkError(''); }} />
                )}
              </Field>
              <button type="button" onClick={runBulk} disabled={bulkBusy} className={`btn-secondary ${bulkError ? 'mb-5' : ''}`}>
                {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Re-stamp
              </button>
            </div>
            <button type="button" onClick={saveDefaults} disabled={savingDefaults} className="btn-primary">
              {savingDefaults ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save defaults
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Per-service-type datagrid */}
      <SectionCard title={`Per-service-type rates · ${rows.length}`} icon={Route}
        actions={<span className="flex items-center gap-1.5 text-[12px] text-slate-500"><Info size={13} className="text-slate-400" aria-hidden="true" /> A blank rate on a shift uses these</span>}>
        {rows.length === 0 ? (
          <EmptyState icon={Route} title="No service types yet"
            hint="Service types come from the shift types in Financial configuration. Add one there and it appears here, ready for its caps and rates."
            action={<Link to="/financials/config" className="btn-secondary">Open financial configuration</Link>} />
        ) : (
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead>
                <tr className={THEAD_ROW}>
                  <th scope="col" className={`${TH} sticky left-0 z-[1] bg-white`}>Service type</th>
                  <th scope="col" className={TH_NUM}>Max travel km</th>
                  <th scope="col" className={TH_NUM}>Max travel hours</th>
                  <th scope="col" className={TH_NUM}>Max transport km</th>
                  <th scope="col" className={TH_NUM}>Travel time $/hr</th>
                  <th scope="col" className={TH_NUM}>Travel $/km</th>
                  <th scope="col" className={TH_NUM}>Transport $/km</th>
                  <th scope="col" className={TH}>Travel code</th>
                  <th scope="col" className={TH}>Transport code</th>
                  <th scope="col" className={`${TH} text-center`}>Therapy</th>
                  <th scope="col" className={TH_NUM}>Therapy %</th>
                  <th scope="col" className={TH}><span className="sr-only">Save</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const name = codeLabel(r.shift_type) || r.shift_type;
                  return (
                    <tr key={r.id} className={TR}>
                      <td className={`${CELL} sticky left-0 z-[1] bg-white px-4 font-medium text-slate-800 whitespace-nowrap`} title={r.shift_type || ''}>{name}</td>
                      <td className={CELL}><NumCell v={r.max_travel_km} label={`${name}: max travel km`} on={v => patchRow(r.id, 'max_travel_km', v)} /></td>
                      <td className={CELL}><NumCell v={r.max_travel_hours} label={`${name}: max travel hours`} on={v => patchRow(r.id, 'max_travel_hours', v)} /></td>
                      <td className={CELL}><NumCell v={r.max_transport_km} label={`${name}: max transport km`} on={v => patchRow(r.id, 'max_transport_km', v)} /></td>
                      <td className={CELL}><NumCell v={r.travel_time_rate} label={`${name}: travel time rate per hour`} on={v => patchRow(r.id, 'travel_time_rate', v)} /></td>
                      <td className={CELL}><NumCell v={r.travel_km_rate} label={`${name}: travel rate per km`} on={v => patchRow(r.id, 'travel_km_rate', v)} /></td>
                      <td className={CELL}><NumCell v={r.transport_km_rate} label={`${name}: transport rate per km`} on={v => patchRow(r.id, 'transport_km_rate', v)} /></td>
                      <td className={CELL}><input list="ndis-transport-items" placeholder="Auto (price book)" aria-label={`${name}: travel code`} className="h-9 w-36 rounded-md border border-slate-300 bg-white px-2 font-mono text-xs text-slate-900 placeholder:font-sans placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" value={r.travel_km_code || ''} onChange={e => patchRow(r.id, 'travel_km_code', e.target.value)} /></td>
                      <td className={CELL}><input list="ndis-transport-items" placeholder="Auto (price book)" aria-label={`${name}: transport code`} className="h-9 w-36 rounded-md border border-slate-300 bg-white px-2 font-mono text-xs text-slate-900 placeholder:font-sans placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" value={r.transport_km_code || ''} onChange={e => patchRow(r.id, 'transport_km_code', e.target.value)} /></td>
                      <td className={`${CELL} text-center`}><input type="checkbox" aria-label={`${name}: therapy`} checked={!!r.is_therapy} onChange={e => patchRow(r.id, 'is_therapy', e.target.checked)} className="h-4 w-4 rounded accent-primary-600" /></td>
                      <td className={CELL}><NumCell v={r.therapy_travel_pct} label={`${name}: therapy travel %`} on={v => patchRow(r.id, 'therapy_travel_pct', v)} /></td>
                      <td className={`${CELL} pr-4`}>
                        <button type="button" onClick={() => saveRow(r)} disabled={savingId === r.id} aria-label={`Save ${name}`} title={`Save ${name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary-700 hover:bg-primary-50 disabled:opacity-50">
                          {savingId === r.id ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* NDIS transport item suggestions for both km-code columns (km_reference from the price book) */}
        <datalist id="ndis-transport-items">
          {transportItems.map(t => <option key={t.kmReference} value={t.kmReference}>{t.label}</option>)}
        </datalist>
        {rows.length > 0 && (
          <p className="px-4 py-2.5 text-[12px] leading-relaxed text-slate-500 border-t border-slate-100">
            Leave a code blank to use the price book's transport item (km_reference) for that shift. Set a code here to force a specific NDIS transport item for a service type (for example 01_799… or 04_799…). Save each row after changing it.
          </p>
        )}
      </SectionCard>
    </div>
  );
}

function NumCell({ v, on, label }) {
  return (
    <input type="number" step="0.01" aria-label={label} value={v ?? ''} onChange={e => on(e.target.value)}
      className="h-9 w-20 rounded-md border border-slate-300 bg-white px-2 text-right text-sm tabular-nums text-slate-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
  );
}
